import { useState, type MouseEvent } from 'react';
import * as Sentry from '@sentry/react';
import { useNotification, useTranslation } from '@refinedev/core';
import { ActionIcon, Group, Loader, Tooltip } from '@mantine/core';
import { IconDownload, IconEye } from '@tabler/icons-react';
import { generateGetSignedUrl } from 'api/action';
import { getReportByID } from 'api/resource/report';
import { downloadFromS3 } from 'hooks/useDownloadFromS3';
import type { Report } from 'interfaces';
import { ReportPreviewModal } from './ReportPreviewModal';

interface ReportNotificationActionsProps {
  reportID: string;
  onMarkRead: () => void;
  onPreview?: (url: string) => void;
}

const REPORT_BUCKET = `report-files-${import.meta.env.VITE_ACCOUNT_ID}`;

export const ReportNotificationActions = ({
  reportID,
  onMarkRead,
  onPreview,
}: ReportNotificationActionsProps) => {
  const { translate } = useTranslation();
  const { open: openNotification } = useNotification();
  const [report, setReport] = useState<Report | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewOpened, setPreviewOpened] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const loadReport = async (): Promise<Report> => {
    if (report) {
      return report;
    }

    const loadedReport = await getReportByID(reportID);
    if (!loadedReport?.s3Path) {
      throw new Error('Report file is not available.');
    }

    setReport(loadedReport);
    return loadedReport;
  };

  const handleError = (error: unknown) => {
    Sentry.captureException(error);
    openNotification?.({
      message: translate(
        'common:notificationCenter.reportActions.unavailable',
        'The report file is unavailable. Please try again.'
      ),
      type: 'error',
    });
  };

  const prepareAction = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onMarkRead();
  };

  const previewReport = async (event: MouseEvent<HTMLButtonElement>) => {
    prepareAction(event);
    try {
      setPreviewLoading(true);
      const loadedReport = await loadReport();
      if (!loadedReport.s3Path.toLowerCase().endsWith('.pdf')) {
        openNotification?.({
          message: translate(
            'common:notificationCenter.reportActions.pdfOnly',
            'Preview is available for PDF reports only.'
          ),
          type: 'error',
        });
        return;
      }

      const url = await generateGetSignedUrl({
        bucket: REPORT_BUCKET,
        key: encodeURIComponent(loadedReport.s3Path),
      });
      if (onPreview) {
        onPreview(url);
      } else {
        setPreviewUrl(url);
        setPreviewOpened(true);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const downloadReport = async (event: MouseEvent<HTMLButtonElement>) => {
    prepareAction(event);
    try {
      setDownloadLoading(true);
      const loadedReport = await loadReport();
      await downloadFromS3(
        REPORT_BUCKET,
        encodeURIComponent(loadedReport.s3Path)
      );
    } catch (error) {
      handleError(error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const previewDisabled =
    previewLoading ||
    Boolean(report && !report.s3Path.toLowerCase().endsWith('.pdf'));

  return (
    <>
      <Group gap="xs" mt={4}>
        <Tooltip
          label={translate(
            'common:notificationCenter.reportActions.preview',
            'Preview report'
          )}
        >
          <ActionIcon
            variant="light"
            aria-label={translate(
              'common:notificationCenter.reportActions.preview',
              'Preview report'
            )}
            disabled={previewDisabled}
            onClick={previewReport}
          >
            {previewLoading ? <Loader size={16} /> : <IconEye size={16} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip
          label={translate(
            'common:notificationCenter.reportActions.download',
            'Download report'
          )}
        >
          <ActionIcon
            variant="light"
            aria-label={translate(
              'common:notificationCenter.reportActions.download',
              'Download report'
            )}
            disabled={downloadLoading}
            onClick={downloadReport}
          >
            {downloadLoading ? (
              <Loader size={16} />
            ) : (
              <IconDownload size={16} />
            )}
          </ActionIcon>
        </Tooltip>
      </Group>
      <ReportPreviewModal
        opened={previewOpened}
        previewUrl={previewUrl}
        onClose={() => setPreviewOpened(false)}
      />
    </>
  );
};
